// =============================================================================
// noir-gold.tsx — premium "Tilla" layout for w04 (magazine variant).
//
// Tone: Vogue / Cartier / Ralph Lauren — gilded, nocturnal, formal.
//
// Treatment notes:
//   - Full-bleed photograph in deep ink with a duotone-style multiply layer
//     and a soft radial vignette so type can breathe.
//   - "Foil-gold" typography for the names: a horizontal gold-leaf gradient
//     clipped to the text, paired with a barely-there warm drop-shadow that
//     gives the embossed-foil feel without bitmap textures.
//   - Hand-built monogram crest from the couple's initials wrapped in a
//     filigree wreath. This is Tilla's signature flourish — referenced again
//     across the magazine variant.
//   - Subtle gold-shimmer animation on first load; pauses cleanly when the
//     visitor has prefers-reduced-motion: reduce.
// =============================================================================

import * as O from "@/components/Ornaments";
import { Photo, CoupleSilhouette, type LayoutCtx } from "./_shared";

// Reusable foil-gold gradient — horizontal sweep through champagne / brass /
// pale-gold, clipped to text via background-clip. The slight drop-shadow on
// the text element above gives it the "stamped foil" depth.
function foilTextStyle(accent: string) {
  return {
    backgroundImage: `linear-gradient(100deg, ${accent} 0%, #f4d98a 22%, #fff7dc 38%, #b88638 55%, #f4d98a 72%, ${accent} 100%)`,
    backgroundSize: "200% 100%",
    WebkitBackgroundClip: "text" as const,
    backgroundClip: "text" as const,
    WebkitTextFillColor: "transparent" as const,
    color: "transparent",
    textShadow: "0 1px 0 rgba(201,162,78,0.18), 0 0 18px rgba(201,162,78,0.12)",
  };
}

// Filigree monogram crest — couple's initials inside a layered wreath with
// flourishes. Tilla's signature element; only ever used in noir-gold.
function MonogramCrest({ initials, color, size = 132 }: { initials: string; color: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 132 132" fill="none" aria-hidden="true">
      <defs>
        <linearGradient id="ng-foil" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={color} />
          <stop offset="50%" stopColor="#f4d98a" />
          <stop offset="100%" stopColor={color} />
        </linearGradient>
      </defs>
      <g stroke="url(#ng-foil)" strokeWidth="0.6" fill="none">
        <circle cx="66" cy="66" r="58" />
        <circle cx="66" cy="66" r="52" opacity="0.55" />
        <circle cx="66" cy="66" r="44" opacity="0.3" />
        {/* eight radial flourish ticks */}
        {Array.from({ length: 8 }).map((_, i) => {
          const a = (i * 45 * Math.PI) / 180;
          const x1 = 66 + Math.cos(a) * 58;
          const y1 = 66 + Math.sin(a) * 58;
          const x2 = 66 + Math.cos(a) * 64;
          const y2 = 66 + Math.sin(a) * 64;
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />;
        })}
        {/* upper laurel sprig */}
        <path d="M30 66 Q 38 50 48 50 Q 58 50 66 38" />
        <path d="M102 66 Q 94 50 84 50 Q 74 50 66 38" />
        {/* lower laurel sprig */}
        <path d="M30 66 Q 38 82 48 82 Q 58 82 66 94" />
        <path d="M102 66 Q 94 82 84 82 Q 74 82 66 94" />
      </g>
      {/* tiny leaf accents */}
      {[36, 96].map((x) => (
        <g key={x}>
          <ellipse cx={x} cy="58" rx="2" ry="3.6" fill="url(#ng-foil)" opacity="0.7" />
          <ellipse cx={x} cy="74" rx="2" ry="3.6" fill="url(#ng-foil)" opacity="0.7" />
        </g>
      ))}
      {/* center dot */}
      <circle cx="66" cy="66" r="1.6" fill={color} />
      {/* the initials */}
      <text
        x="66"
        y="74"
        textAnchor="middle"
        fontFamily="Italiana, serif"
        fontSize="32"
        fill="url(#ng-foil)"
        letterSpacing="2"
      >
        {initials}
      </text>
    </svg>
  );
}

export function NoirGoldLayout({ names, date, venue, accent, ink, bg, font, photo }: LayoutCtx) {
  // Pull initials for the crest. Falls back gracefully if names is non-standard.
  const parts = names.split("&").map((s) => s.trim());
  const initials = `${(parts[0] || "")[0] || "T"}${(parts[1] || "")[0] || "L"}`.toUpperCase();

  return (
    <div className="tilla-layout" style={{ position: "absolute", inset: 0, background: bg, overflow: "hidden" }}>
      {/* Local CSS — shimmer keyframes guarded by prefers-reduced-motion. */}
      <style>{`
        @keyframes ng-shimmer {
          0%   { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
        @keyframes ng-fade-in {
          0% { opacity: 0; transform: translateY(6px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .ng-foil-anim {
          animation: ng-shimmer 7s linear infinite;
        }
        .ng-fade { animation: ng-fade-in 1100ms ease-out both; }
        .ng-fade-2 { animation: ng-fade-in 1300ms ease-out 180ms both; }
        .ng-fade-3 { animation: ng-fade-in 1300ms ease-out 360ms both; }
        @media (prefers-reduced-motion: reduce) {
          .ng-foil-anim,
          .ng-fade,
          .ng-fade-2,
          .ng-fade-3 { animation: none !important; }
        }
        /* ─── Mobile (≤768px) — premium portrait reframing ─────────────── */
        @media (max-width: 768px) {
          .tilla-layout .ng-foil-anim {
            /* Tighter foil glow on small text so it stays crisp on phones */
            text-shadow: 0 1px 0 rgba(201,162,78,0.22), 0 0 8px rgba(201,162,78,0.18) !important;
            font-size: 26px !important;
            letter-spacing: -0.01em !important;
          }
          .tilla-layout .ng-cover-padding {
            padding: 14% 7% !important;
          }
        }
      `}</style>

      {/* Cinematic photograph — desaturated to match the deep-ink palette. */}
      <Photo
        src={photo}
        fallback={<CoupleSilhouette tone={accent} accent={accent} bg={bg} />}
        style={{
          position: "absolute",
          inset: 0,
          opacity: 0.62,
          filter: "grayscale(0.35) contrast(1.05) brightness(0.78)",
        }}
      />

      {/* Duotone tint — multiplies a deep-ink wash to push the photo nocturnal. */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: `linear-gradient(180deg, ${ink}aa 0%, ${ink}55 35%, ${ink}aa 100%)`,
          mixBlendMode: "multiply",
        }}
      />

      {/* Soft vignette — frames the type. */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: `radial-gradient(ellipse at center, transparent 30%, ${bg} 100%)`,
        }}
      />

      {/* Double gold border — magazine-cover proportions. */}
      <div style={{ position: "absolute", inset: "5%", border: `0.6px solid ${accent}` }} />
      <div style={{ position: "absolute", inset: "6%", border: `0.5px solid ${accent}33` }} />

      {/* Decorative corner ticks for that printed-stationery feel. */}
      {([
        { top: "6%", left: "6%", deg: 0 },
        { top: "6%", right: "6%", deg: 90 },
        { bottom: "6%", right: "6%", deg: 180 },
        { bottom: "6%", left: "6%", deg: 270 },
      ] as const).map(({ deg, ...pos }, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            width: 18,
            height: 18,
            ...pos,
            transform: `rotate(${deg}deg)`,
          }}
        >
          <svg viewBox="0 0 18 18" width="18" height="18">
            <path d="M0 0 L18 0 L18 1 L1 1 L1 18 L0 18 Z" fill={accent} opacity="0.85" />
          </svg>
        </div>
      ))}

      <div
        className="ng-cover-padding"
        style={{
          position: "absolute",
          inset: 0,
          padding: "12% 9%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        {/* Top — small ornament + edition stamp. */}
        <div className="ng-fade" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
          <O.Suzani size={26} color={accent} />
          <div
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 6.5,
              fontWeight: 700,
              letterSpacing: "0.5em",
              color: `${accent}cc`,
            }}
          >
            EXCLUSIVE EDITION
          </div>
        </div>

        {/* Center — monogram crest + foil-gold names + date stamp. */}
        <div className="ng-fade-2" style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <div style={{ marginBottom: "5%" }}>
            <MonogramCrest initials={initials} color={accent} size={94} />
          </div>
          <div
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 6.5,
              fontWeight: 700,
              letterSpacing: "0.55em",
              color: `${accent}cc`,
              marginBottom: "5%",
            }}
          >
            THE WEDDING OF
          </div>
          <div
            className="ng-foil-anim"
            style={{
              ...foilTextStyle(accent),
              fontFamily: font,
              fontSize: 30,
              lineHeight: 1,
              fontWeight: 400,
              letterSpacing: "-0.005em",
            }}
          >
            {names}
          </div>
          <div
            style={{
              height: 1,
              width: "44%",
              background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
              margin: "7% auto",
            }}
          />
          <div
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 8,
              fontWeight: 600,
              letterSpacing: "0.4em",
              color: ink,
            }}
          >
            {date}
          </div>
        </div>

        {/* Bottom — venue with hairline accents either side. */}
        <div
          className="ng-fade-3"
          style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}
        >
          <span style={{ width: 22, height: 1, background: accent, opacity: 0.6 }} />
          <span
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 7,
              fontWeight: 700,
              letterSpacing: "0.32em",
              color: `${ink}d0`,
            }}
          >
            {venue.toUpperCase()}
          </span>
          <span style={{ width: 22, height: 1, background: accent, opacity: 0.6 }} />
        </div>
      </div>
    </div>
  );
}
